import { Button, Dialog, DialogContent, DialogTitle, IconButton } from "@material-ui/core";
import { CloudDownload, Print, Visibility, PhoneIphone, Email } from "@material-ui/icons";
import CloseIcon from '@material-ui/icons/Close';
import { renderDate } from "common/services/util";
import { getStockData } from "common/store/reducers/stock";
import { getTasksData } from "common/store/reducers/tasks";
import SearchField from "invoices/components/SearchField";
import SwitchEdit from "invoices/components/SwitchEdit";
import IvaSwitch from "invoices/components/IvaSwitch";
import TableInvoiceDetail from "invoices/components/TableInvoiceDetail";
import TextFieldEdit from "invoices/components/TextFieldEdit";
import { columnsInvoiceDetail, mapPaymentMode } from "invoices/services/config";
import PaymentModeButtons from "invoices/components/PaymentModeButtons";
import createPDFInvoiceUseCase from "invoices/services/createPDFInvoice";
import { calculateTotalInvoice, calculateTotalPrice } from "invoices/services/detail/calculateInvoicesInfo";
import updateInvoiceDetailUseCase from "invoices/services/detail/updateInvoiceDetailUseCase";
import sendInvoiceByEmailUseCase from "invoices/services/sendInvoiceByEmailUseCase";
import filterItemsUseCase from "invoices/services/filterItemsUseCase";
import React, { useEffect, useState } from 'react';
import { connect } from "react-redux";
import findClientByTask from "tasks/services/sign/findClientByTask";
import { v4 as uuidv4 } from 'uuid';

const InvoiceDetailContainer = ({
    open,
    dataInvoice,
    dataDetail,
    onClose,
    stockList,
    isProforma,
    taskList,
    onChangePaymentMode
}) => {

    const initialData = items => {
        return items.map(item => {
            return {
                ...item,
                id: uuidv4(),
                totalPrice: calculateTotalPrice(item)
            }
        })
    }

    const initial = initialData(dataDetail.items);
    const [data, setData] = useState(initial);
    const [allIva, setAllIva] = useState(() =>
        initial.filter(r => !r.isEmpty).every(r => r.iva === true)
    );

    const [dataRender, setDataRender] = useState([])
    const [client, setClient] = useState({})
    const [timeoutId, setTimeoutId] = useState(null)

    useEffect(() => {
        const dataRender = [...data, ...new Array(1).fill({ isEmpty: true, iva: true })]
        setDataRender(dataRender)

        clearTimeout(timeoutId)
        const id = setTimeout(() => updateInvoiceDetailUseCase(dataInvoice, dataInvoice.ivaPercent, { data }), 1000)
        setTimeoutId(id)
    }, [data])

    useEffect(() => {
        if (!dataInvoice.dniClient) {
            return
        }

        findClientByTask(dataInvoice.dniClient).then(clientData => {
            setClient(clientData)

            // Solo aplicar preferencia del cliente en facturas nuevas (sin iva definido en los items)
            const isNewInvoice = initial.filter(r => !r.isEmpty).every(r => r.iva === undefined)
            if (isNewInvoice && clientData.iva === false) {
                setAllIva(false)
                setData(curr => curr.map(r => r.isEmpty ? r : { ...r, iva: false }))
            }
        })
    }, [])

    const toggleAllIva = () => {
        setAllIva(prev => {
            const next = !prev;
            setData(curr => {
                curr.forEach(r => {
                    if (!r.isEmpty) r.iva = next;
                });
                return [...curr];
            });

            clearTimeout(timeoutId);
            const id = setTimeout(() => {
                updateInvoiceDetailUseCase(dataInvoice, dataInvoice.ivaPercent, { data });
            }, 1000);
            setTimeoutId(id);

            return next;
        });
    };

    const handleInvoiceItemUpdate = async (item) => {
        if (item.isEmpty) {
            const newItem = {
                ...item,
                isEmpty: false,
                id: uuidv4(),
                iva: allIva,
                totalPrice: calculateTotalPrice(item)
            }
            setData((prevState) => [...prevState, newItem])
            return
        }

        setData((prevState) => {
            item = { ...item, totalPrice: calculateTotalPrice(item) }
            const dataUpdated = prevState
                .map(itemData => itemData.id === item.id ? item : itemData)

            return dataUpdated
        })
    }

    const handleInvoiceItemDelete = async (item) => {
        const newData = data.filter(i => i.id !== item.id)
        setDataRender([])
        setData(newData)
    }

    const handleInvoicePrint = async (operation) => {
        createPDFInvoiceUseCase({
            invoiceData: dataInvoice,
            initialInvoiceDetailData: { data },
            operation,
            isProforma
        })
    }

    const textField = (item, field, isNumeric, isMoney) => {
        const handle = value => {
            item[field] = value
            handleInvoiceItemUpdate(item)
        }

        return (
            <TextFieldEdit
                handleChange={handle}
                initialValue={item[field]}
                isNumeric={isNumeric}
                isMoney={isMoney}
            />
        )
    }

    const switchField = (item, field) => {
        const handle = value => {
            item[field] = value
            handleInvoiceItemUpdate(item)
        }

        return (
            <IvaSwitch
                checked={Boolean(item[field])}
                onChange={event => handle(event.target.checked)}
            />
        )
    }

    const searchField = item => {
        const stockToSearch = getStockData(stockList)
        const taskToSearch = getTasksData(taskList).map(task => {
            return { ...task, pvp: task.budget }
        })
        const itemsToSearch = [...stockToSearch, ...taskToSearch].sort((s1, s2) => s2.reference - s1.reference)

        const handle = ({ reference, description, pvp }) => {
            item.reference = reference

            if (description) {
                item.description = description
            }

            if (!item.units) {
                item.units = 1
            }

            if (pvp) {
                item.unitPrice = pvp
            }

            handleInvoiceItemUpdate(item)
        }

        return (
            <SearchField
                textInitial={item.reference}
                onTextRender={item => item.reference}
                items={itemsToSearch}
                onFilterItems={(itemList, textFilter) =>
                    filterItemsUseCase({
                        itemList,
                        textFilter,
                        fieldsFilter: ["reference", "description"]
                    })
                }
                onSelectItem={handle}
                onRenderItem={({ reference, description }) => `${reference}-${description}`}
            />
        )
    }

        const handleInvoiceEmail = async () => {
            try {
                await sendInvoiceByEmailUseCase({
                    invoiceData: dataInvoice,
                    invoiceDetail: data,
                    isProforma,
                });

                alert(isProforma 
                    ? "Proforma enviada por email ✅" 
                    : "Factura enviada por email ✅"
                );
            } catch (err) {
                if (err.message === "Este cliente no tiene email configurado") {
                    alert(err.message);
                } else {
                    console.error("❌ Error al enviar factura por email", err);
                    alert("Error al enviar la factura por email");
                }
            }
        };

      const columns = columnsInvoiceDetail({
        textField,
        switchField,
        searchField,
        headerIvaChecked: allIva,
        toggleAllIva
    });


    const infoInvoice = calculateTotalInvoice(data, dataInvoice.ivaPercent)

    return (
        <Dialog
            fullScreen={window.innerHeight < 900}
            fullWidth={true}
            maxWidth={'lg'}
            open={open}
        >
            <IconButton onClick={onClose} style={{ position: 'absolute', right: '5px', top: '5px' }}>
                <CloseIcon />
            </IconButton>
            <DialogTitle>
                <div style={{ maxHeight: '80px', textWrap: 'nowrap', fontSize: window.innerWidth > 600 ? '20px' : '4vw' }}>
                    <div>{isProforma ? 'Proforma' : 'Factura'}: {dataInvoice.numInvoice} - {renderDate(dataInvoice.invoiceDate)} - {dataInvoice.nameClient}</div>
                    {dataInvoice.dniClient && <div> {dataInvoice.dniClient} - {client.mail}</div>}
                    {dataInvoice.dniClient && <div>C/{client.street},{client.postcode},{client.locality}({client.community})</div>}
                </div>
            </DialogTitle>
            <DialogContent>
                <TableInvoiceDetail
                    columns={columns}
                    data={dataRender}
                    onInvoiceItemDelete={handleInvoiceItemDelete}
                    margins={[140, 300]}
                />
                <div style={{ display: 'flex', marginTop: '20px', marginBottom: '20px', justifyContent: 'space-between' }}>
                    <div style={{ width: '200px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Base:</span>
                            <span>{infoInvoice.base} €</span>
                        </div>
                        <div style={{ display: 'flex', flex: 1, justifyContent: 'space-between' }}>
                            <span>IVA:</span>
                            <span>{infoInvoice.ivaPrice} €</span>
                        </div>
                        <div style={{ display: 'flex', flex: 1, justifyContent: 'space-between', fontWeight: 'bold', fontSize: '20px' }}>
                            <span>TOTAL:</span>
                            <span>{infoInvoice.total} €</span>
                        </div>
                    </div>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 'bold' }}>Tipo de pago:</span>
                        <PaymentModeButtons
                            value={Number(dataInvoice?.paymentMode ?? 1)}
                            onChange={(val) => onChangePaymentMode(dataInvoice, Number(val))}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '500px' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            style={{ width: '90px', height: '100%' }}
                            onClick={() => handleInvoicePrint("view")}
                        >
                            <Visibility />
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            style={{ width: '90px', height: '100%' }}
                            onClick={() => handleInvoicePrint("save")}
                        >
                            <CloudDownload />
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            style={{ width: '90px', height: '100%' }}
                            onClick={() => handleInvoicePrint("print")}
                        >
                            <Print />
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            style={{ width: '90px', height: '100%' }}
                            onClick={() => handleInvoicePrint("upload")}
                        >
                            <PhoneIphone />
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            style={{ width: '90px', height: '100%' }}
                            onClick={handleInvoiceEmail}
                        >
                            <Email />
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}

const mapStateToProps = state => ({
    stockList: state.stock.data,
    taskList: state.tasks.data
})

export default connect(mapStateToProps, null)(InvoiceDetailContainer)